import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMonetization } from '../contexts/MonetizationContext';

const StoryViewPage = () => {
  const navigate = useNavigate();
  const { storyId } = useParams();
  const location = useLocation();
  const { api } = useAuth();
  const { isPremium } = useMonetization();
  
  const [story, setStory] = useState(null);
  const [storyPages, setStoryPages] = useState([]);
  const [storyTitle, setStoryTitle] = useState('Histoire Magique');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageIllustrations, setPageIllustrations] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const textScrollRef = useRef(null);

  // Reset scroll quand on change de page
  useEffect(() => {
    if (textScrollRef.current) {
      textScrollRef.current.scrollTop = 0;
    }
  }, [currentPage]);

  // Charger l'histoire
  useEffect(() => {
    const loadStory = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Si l'histoire est passée via location.state (depuis la génération)
        if (location.state?.story) {
          console.log('📖 Histoire reçue via state:', location.state.story);
          processStory(location.state.story, location.state.pageIllustrations || {});
          return;
        }
        
        // Sinon, charger depuis l'API
        if (storyId) {
          console.log('📖 Chargement histoire depuis API:', storyId);
          const response = await api.get(`/stories/${storyId}`);
          if (response.data) {
            processStory(response.data, response.data.page_illustrations || {});
          } else {
            throw new Error('Histoire non trouvée');
          }
        }
      } catch (err) {
        console.error('Erreur chargement histoire:', err);
        setError('Impossible de charger l\'histoire');
      } finally {
        setIsLoading(false);
      }
    };

    loadStory();
  }, [storyId, location.state]);

  // Traitement et parsing de l'histoire
  const processStory = (storyData, illustrations) => {
    let pages = [];
    let title = 'Histoire Magique';
    
    try {
      let parsedData = null;
      
      if (storyData?.story?.content) {
        let content = storyData.story.content;
        content = content.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
        parsedData = JSON.parse(content);
        title = storyData.story.title || parsedData.title || 'Histoire Magique';
      }
      else if (storyData?.content) {
        let content = storyData.content;
        content = content.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
        try {
          parsedData = JSON.parse(content);
          title = storyData.title || parsedData.title || 'Histoire Magique';
        } catch (e) {
          pages = [{ page: 1, content: content }];
          title = storyData.title || 'Histoire Magique';
        }
      }
      else if (storyData?.pages) {
        parsedData = storyData;
        title = storyData.title || 'Histoire Magique';
      }
      
      if (parsedData?.pages) {
        pages = parsedData.pages;
        title = parsedData.title || title;
      }
      
      // Utiliser les illustrations fournies ou celles de l'histoire
      let finalIllustrations = illustrations;
      if (storyData?.page_illustrations && Object.keys(storyData.page_illustrations).length > 0) {
        finalIllustrations = { ...storyData.page_illustrations, ...illustrations };
      }
      if (storyData?.story?.page_illustrations) {
        finalIllustrations = { ...storyData.story.page_illustrations, ...finalIllustrations };
      }
      
      setPageIllustrations(finalIllustrations);
      
    } catch (e) {
      console.error('Erreur parsing histoire:', e);
      pages = [{ page: 1, content: 'Erreur lors du chargement de l\'histoire.' }];
    }
    
    if (pages.length === 0) {
      pages = [{ page: 1, content: 'Contenu non disponible.' }];
    }
    
    setStoryPages(pages);
    setStoryTitle(title);
    setStory(storyData);
    setCurrentPage(0);
  };

  const handleBack = () => {
    if (location.state?.fromHistory) {
      navigate('/history');
    } else {
      navigate('/story');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-200 border-t-teal-500 mx-auto mb-4"></div>
          <p className="text-teal-700 font-medium">Chargement de l'histoire...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
        <div className="text-center">
          <div className="text-5xl mb-4">😕</div>
          <p className="text-gray-700 font-medium mb-4">{error}</p>
          <button onClick={handleBack} className="px-6 py-3 bg-teal-500 text-white rounded-xl font-medium hover:bg-teal-600 transition-colors">
            Retour
          </button>
        </div>
      </div>
    );
  }

  const currentPageData = storyPages[currentPage] || storyPages[0];
  const pageContent = typeof currentPageData === 'string' 
    ? currentPageData 
    : currentPageData?.content || 'Contenu non disponible';

  return (
    <>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap');`}
      </style>
      
      <div className="h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #B8D4E3 0%, #A7D3D8 25%, #9ECDC4 50%, #A8D5BA 75%, #B5DCAE 100%)' }}>
        
        {/* Header fixe avec bouton retour et titre */}
        <div className="flex-shrink-0 p-4 pb-2">
          <button
            onClick={handleBack}
            className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm shadow-lg flex items-center justify-center text-gray-700 hover:bg-white transition-colors z-10"
          >
            ←
          </button>
          
          <div className="text-center">
            <h1 
              className="text-2xl sm:text-3xl font-bold text-gray-800 px-12"
              style={{ fontFamily: '"Caveat", cursive', textShadow: '0 2px 4px rgba(255,255,255,0.5)' }}
            >
              {storyTitle}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Page {currentPage + 1} sur {storyPages.length}
            </p>
          </div>
        </div>

        {/* Image - même largeur que le bloc de texte */}
        <div className="flex-shrink-0 px-4 pb-3">
          <div className="max-w-2xl mx-auto">
            {pageIllustrations[String(currentPage + 1)] ? (
              <img
                src={`data:image/png;base64,${pageIllustrations[String(currentPage + 1)]}`}
                alt={`Illustration page ${currentPage + 1}`}
                className="w-full rounded-2xl shadow-2xl"
                style={{ 
                  maxHeight: '50vh',
                  objectFit: 'contain',
                  objectPosition: 'center'
                }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center space-y-3 bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg w-full h-48">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-teal-200 border-t-teal-500"></div>
                <div className="text-teal-700 font-medium text-sm">Génération de l'illustration...</div>
              </div>
            )}
          </div>
        </div>

        {/* Zone de texte scrollable */}
        <div 
          ref={textScrollRef}
          className="flex-1 overflow-y-auto px-4 pb-4"
        >
          <div className="max-w-2xl mx-auto">
            {/* Texte de l'histoire */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 shadow-lg mb-4">
              <div className="text-gray-800 leading-relaxed text-base whitespace-pre-wrap">
                {pageContent}
              </div>
              
              {/* "FIN" et bannière upgrade sur la dernière page */}
              {currentPage === storyPages.length - 1 && (
                <div className="text-center mt-6">
                  <div 
                    className="text-3xl font-bold tracking-wider mb-4"
                    style={{
                      fontFamily: '"Caveat", cursive',
                      background: 'linear-gradient(45deg, #0EA5E9, #14B8A6)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Fin
                  </div>
                  
                  {/* Bannière émotionnelle post-histoire (utilisateurs gratuits uniquement) */}
                  {!isPremium && (
                    <div 
                      data-testid="story-upgrade-banner"
                      className="mt-4 p-5 rounded-2xl text-left"
                      style={{ 
                        background: 'linear-gradient(135deg, #FFF7ED 0%, #FEF3C7 100%)',
                        border: '1px solid #FDE68A'
                      }}
                    >
                      <p className="text-gray-800 text-sm font-semibold mb-1" style={{ fontFamily: '"Caveat", cursive', fontSize: '1.1rem' }}>
                        {story?.child_name 
                          ? `🌟 ${story.child_name} a encore des questions !`
                          : `🌟 Votre enfant a encore des questions !`
                        }
                      </p>
                      <p className="text-gray-700 text-sm leading-relaxed mb-3">
                        Passez à Explorateur pour répondre à tous ses "pourquoi"… sans limite et sans jamais le laisser sans réponse.
                      </p>
                      <button
                        onClick={() => navigate('/subscription')}
                        data-testid="story-upgrade-cta"
                        className="w-full py-3 rounded-full font-bold text-white text-sm transition-all hover:opacity-90 active:scale-[0.98]"
                        style={{ background: 'linear-gradient(135deg, #4A90A4 0%, #5BBFBD 100%)' }}
                      >
                        Découvrir Explorateur
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation fixe en bas */}
        <div className="flex-shrink-0 px-4 py-3 bg-gradient-to-t from-[#B5DCAE]/90 to-transparent">
          <div className="max-w-2xl mx-auto">
            <div className="flex justify-center items-center gap-4">
              {/* Bouton précédent */}
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                  currentPage === 0 
                    ? 'bg-white/40 text-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-blue-400 to-blue-500 text-white shadow-lg hover:shadow-xl hover:scale-105'
                }`}
              >
                ←
              </button>
              
              {/* Indicateurs de pages */}
              <div className="flex items-center gap-2">
                {storyPages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      index === currentPage 
                        ? 'bg-teal-500 scale-125' 
                        : index < currentPage 
                        ? 'bg-teal-300' 
                        : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
              
              {/* Bouton suivant */}
              <button
                onClick={() => setCurrentPage(Math.min(storyPages.length - 1, currentPage + 1))}
                disabled={currentPage >= storyPages.length - 1}
                className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all ${
                  currentPage >= storyPages.length - 1 
                    ? 'bg-white/40 text-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-teal-400 to-teal-500 text-white shadow-lg hover:shadow-xl hover:scale-105'
                }`}
              >
                →
              </button>
            </div>
            
            {/* Bouton retour */}
            <div className="text-center mt-3">
              <button
                onClick={handleBack}
                className="text-gray-600 hover:text-teal-600 font-medium transition-colors text-sm"
              >
                ← Retour
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StoryViewPage;
